// 首页展示配置模块类型定义

import type { DailyRechargeTask } from "../../../components/Event/components/SevenDayRecharge/SevenDayRecharge.types";
import type { DailyBetTask } from "../../../components/Event/components/BetActivity/BetActivity.types";
// ===========================
// 枚举类型定义
// ===========================

/**
 * 展示位置枚举
 */
export enum DisplayPosition {
  /** 首页主Banner */
  HOME_MAIN_BANNER = "HOME_MAIN_BANNER",
  /** 首页次级Banner（左） */
  HOME_SUB_BANNER_LEFT = "HOME_SUB_BANNER_LEFT",
  /** 首页次级Banner（右） */
  HOME_SUB_BANNER_RIGHT = "HOME_SUB_BANNER_RIGHT",
  /** Events列表 */
  EVENTS_LIST = "EVENTS_LIST",
  /** 右下角浮动图标 */
  FLOATING_ICON = "FLOATING_ICON",
  /** 左下角浮动图标 */
  LEFT_BOTTOM_FLOATING_ICON = "LEFT_BOTTOM_FLOATING_ICON",
}

/**
 * 数据来源类型枚举
 */
export enum SourceType {
  /** 从活动配置同步 */
  ACTIVITY = "ACTIVITY",
  /** 自定义添加 */
  CUSTOM = "CUSTOM",
}

/**
 * 内容展示类型枚举
 */
export enum ContentType {
  /** 跳转链接 */
  REDIRECT_LINK = "REDIRECT_LINK",
  /** 自定义文案 */
  CUSTOM_CONTENT = "CUSTOM_CONTENT",
}

/**
 * 弹窗触发时机类型枚举
 */
export enum TimingType {
  /** 余额不足时弹出 */
  BALANCE_INSUFFICIENT = "BALANCE_INSUFFICIENT",
  /** 进入首页时弹出 */
  ENTER_HOME_PAGE = "ENTER_HOME_PAGE",
  /** 获得活动参与资格时弹出 */
  ACTIVITY_QUALIFICATION = "ACTIVITY_QUALIFICATION",
}

/**
 * 弹窗类别枚举
 */
export enum PopupCategory {
  /** 余额类型弹窗 */
  BALANCE_TYPE = "BALANCE_TYPE",
  /** 自定义内容弹窗 */
  CUSTOM_CONTENT = "CUSTOM_CONTENT",
  /** 活动类型弹窗 */
  ACTIVITY_TYPE = "ACTIVITY_TYPE",
}

// ===========================
// displayItems 相关类型
// ===========================

/**
 * 活动类型枚举
 */
export enum ActivityType {
  /** 自定义首页弹窗 */
  CUSTOM_HOME_POPUP = "CUSTOM_HOME_POPUP",
  /** 红包雨 */
  RED_PACKET_RAIN = "RED_PACKET_RAIN",
  /** 首充 */
  FIRST_DEPOSIT = "FIRST_DEPOSIT",
  /** 充值优惠 */
  RECHARGE_PROMOTION = "RECHARGE_TASK_OLD",
  /** 5冲 */
  FIVE_DAILY_RECHARGE = "FIVE_DEPOSIT",
  /** 7日充值 */
  DAILY_RECHARGE = "DAILY_RECHARGE",
  /** 签到 */
  SIGN_REWARD = "SIGN_REWARD",
  /** bet */
  BET_TASK = "BET_TASK",
  /** 充值活动 */
  RECHARGE_ACTIVITY = "RECHARGE_ACTIVITY",
  /** 救援金 */
  RESCUE_FUND = "RESCUE_FUND",
  /** 九宫格活动 */
  NINE_GRID_ACTIVITY = "NINE_GRID_ACTIVITY",
}

/**
 * Banner配置项 - 活动来源基础字段
 */
interface DisplayItemActivityBase {
  id: string;
  position: DisplayPosition;
  sourceType: SourceType.ACTIVITY;
  imageUrl?: string;
  sort: number;
  activityId: string;
  groupId?: string;
  activityName?: string;
  activityType?: ActivityType;
  platformIds: string[];
  builtInTags: string[];
  categories: string[];
}

/**
 * Banner配置项 - 自定义来源跳转链接
 */
interface DisplayItemCustomRedirect {
  id: string;
  position: DisplayPosition;
  sourceType: SourceType.CUSTOM;
  activityId?: string;
  activityName?: string;
  activityType?: ActivityType;
  imageUrl?: string;
  sort: number;
  contentType: ContentType.REDIRECT_LINK;
  redirectUrl?: string;
  internalRoute?: string;
}

/**
 * Banner配置项 - 自定义来源自定义内容
 */
interface DisplayItemCustomContent {
  id: string;
  position: DisplayPosition;
  sourceType: SourceType.CUSTOM;
  activityId?: string;
  activityName?: string;
  activityType?: ActivityType;
  imageUrl?: string;
  sort: number;
  contentType: ContentType.CUSTOM_CONTENT;
  title: string;
  content: string;
}

/**
 * Banner配置项联合类型
 */
export type DisplayItem =
  | DisplayItemActivityBase
  | DisplayItemCustomRedirect
  | DisplayItemCustomContent;

// ===========================
// popupTimings 相关类型
// ===========================

/**
 * 活动弹窗配置项
 */
export interface ActivityPopupItem {
  activityId: string;
  activityName: string;
  activityType?: ActivityType;
  sort: number;
  enabled: boolean;
  popupHeaderImageUrl?: string;
  groupId: string;
  popupTitle?: string;
  popupSubtitle?: string;
  popupTimingBalance?: number;
  popupDaily?: boolean;
  popupPriority?: number;
  platformIds: string[];
  builtInTags: string[];
  categories: string[];
}

/**
 * 自定义弹窗配置项
 */
export interface CustomPopupItem {
  itemId: string;
  title: string;
  content?: string;
  sort: number;
  enabled: boolean;
}

/**
 * 弹窗时机配置
 */
export interface PopupTiming {
  id: string;
  timingType: TimingType;
  sort: number;
  enabled: boolean;
  activityItems: ActivityPopupItem[];
  customItems: CustomPopupItem[];
}

// ===========================
// popupPriorities 相关类型
// ===========================

/**
 * 弹窗优先级配置 - 基础类型
 */
interface PopupPriorityBase {
  category: PopupCategory;
  maxPopupCount: number;
  sort: number;
  enabled: boolean;
}

/**
 * 弹窗优先级配置 - 自定义内容类型
 */
interface PopupPriorityCustomContent extends PopupPriorityBase {
  category: PopupCategory.CUSTOM_CONTENT;
  customTitle: string;
  customContent: string;
  customEnabled: boolean;
}

/**
 * 弹窗优先级配置 - 其他类型
 */
interface PopupPriorityOther extends PopupPriorityBase {
  category: PopupCategory.BALANCE_TYPE | PopupCategory.ACTIVITY_TYPE;
}

/**
 * 弹窗优先级配置联合类型
 */
export type PopupPriority = PopupPriorityCustomContent | PopupPriorityOther;

// ===========================
// activities 相关类型
// ===========================

/**
 * 可领取档位配置
 */
export interface ClaimableTier {
  id: string;
  created: number;
  modified: number;
  activityId: string;
  tierNumber: number;
  rewardValueType: "FIXED" | "RATIO";
  rechargeAmount: number;
  rewardItemQuantity?: number;
  rewardRatio?: number;
  principalAuditRatio: number;
  bonusAuditRatio: number;
}

/**
 * 领取记录
 */
export interface ClaimRecord {
  id: string;
  created: number;
  modified: number;
  activityId: string;
  tierId: string;
  userId: string;
  claimStatus: string;
  claimTime?: number;
  [key: string]: any;
}

/**
 * 用户进度
 */
export interface UserProgress {
  id: string;
  created: number;
  modified: number;
  countryId?: string;
  activityId: string;
  userId: string;
  participationTime: number;
  lastActiveTime: number;
  cumulativeTopupAmount: number;
  cumulativeBetAmount: number;
  cumulativeDiscountAmount: number;
  participationStatus: string;
  periodStartTime: number;
  periodEndTime: number;
  lastClaimedItemNumber?: number;
  countdownTime: number;
  // 九宫格抽奖专用字段
  currentRoundNumber?: number; // 当前轮次
  availableLotteryCount?: number; // 可用抽奖次数
  isUnread?: boolean;
  pendingClaimTimes?: number;
  nextTaskInfo?: {
    roundNumber: number;
    nextItemNumber: number;
    rechargeAmount: number;
    actualCumulativeAmount: number;
    currentCumulativeAmount: number;
  };
}

/**
 * 活动入口配置
 */
export interface ActivityEntryPoint {
  id: string;
  created: number;
  modified: number;
  activityId: string;
  entryPointTypes: string[];
  homepageMainBannerImageUrl?: string;
  homepageLeftBannerImageUrl?: string;
  homepageRightBannerImageUrl?: string;
  activityListBannerImageUrl?: string;
  floatingIconImageUrl?: string;
  popupTimingTypes?: string[];
  popupTimingBalance?: number;
  popupDaily?: boolean;
  popupTitle?: string;
  popupSubtitle?: string;
  popupHeaderImageUrl?: string;
  landingPageTitle?: string;
  landingPageContent?: string;
}

/**
 * 分发配置
 */
export interface DistributionConfig {
  distributionMethod: string;
  distributionRatioMode: string;
  batchDistributionBasis?: string;
  batchDayRatios?: Array<{ days: number; ratio: number }>;
  batchBetRatios?: Array<{ betAmount: number; ratio: number }>;
  autoSettleAfterEnd: boolean;
  expireDaysAfterEnd: number;
  maxDiscountAmountType: string;
  maxDiscountAmount: number;
}

/**
 * 领取配置
 */
export interface ClaimConfig {
  tierClaimMode: string;
  isCumulativeRecharge: number;
}

/**
 * 活动项 - 通用活动数据结构
 * 适用于：
 * - DAILY_RECHARGE: 7日充值活动
 * - RECHARGE_ACTIVITY: 充值活动
 * - BET_TASK: 投注任务
 */
export interface ActivityItem {
  id: string;
  created: number;
  modified: number;
  activityGroupId: string;
  countryId?: string;
  activityName?: string;
  validityPeriodType?: string;
  durationAfterJoinHours?: number;
  periodType?: string;
  maxParticipationCount?: number;
  status: string;
  enabled?: boolean;
  groupId: string;
  claimConfig: ClaimConfig;
  distributionConfig: DistributionConfig;
  activityEntryPoint: ActivityEntryPoint;
  claimableTiers: ClaimableTier[];
  claimRecords: ClaimRecord[];
  userProgress: UserProgress;
  [key: string]: any;
}

/**
 * 活动组
 */
export interface ActivityGroup {
  activityGroupId: string;
  groupId: string;
}

/**
 * 充值活动项
 */
export type RechargeActivityItem = ActivityItem;

/**
 * 九宫格活动项
 */
export type NineGridActivityItem = ActivityItem;

/**
 * 7日充值活动配置
 * 使用 DailyRechargeTask 类型（从 SevenDayRecharge 导入）
 */
export interface DailyRechargeActivityConfig {
  list?: DailyRechargeTask[];
  groups?: ActivityGroup[];
}

/**
 * 充值活动配置
 */
export interface RechargeActivityConfig {
  list?: RechargeActivityItem[];
  groups?: ActivityGroup[];
}

/** 九宫格活动 */
export interface NineGridActivityConfig {
  list?: NineGridActivityItem[];
  groups?: ActivityGroup[];
}

/**
 * 投注任务活动配置
 */
export interface BetTaskActivityConfig {
  list?: DailyBetTask[];
  groups?: ActivityGroup[];
}

/**
 * 救援金活动配置
 */
export interface RescueFundActivityConfig {
  list?: ActivityItem[];
  groups?: ActivityGroup[];
}

/**
 * 单个活动类型配置（通用）
 */
export interface ActivityTypeConfig {
  /** 活动列表 */
  list?: ActivityItem[];
  /** 活动组配置 */
  groups?: ActivityGroup[];
}

/**
 * 所有活动配置
 */
export interface ActivitiesConfig {
  /** 充值活动 */
  RECHARGE_ACTIVITY?: RechargeActivityConfig;
  /** 投注任务 */
  BET_TASK?: BetTaskActivityConfig;
  /** 7日充值活动 */
  DAILY_RECHARGE?: DailyRechargeActivityConfig;
  /** 救援金活动 */
  RESCUE_FUND?: RescueFundActivityConfig;
  /** 九宫格活动 */
  NINE_GRID_ACTIVITY?: NineGridActivityConfig;
  /** 其他活动类型 */
  [key: string]:
    | RechargeActivityConfig
    | BetTaskActivityConfig
    | DailyRechargeActivityConfig
    | RescueFundActivityConfig
    | NineGridActivityConfig
    | undefined;
}

// ===========================
// Footer 相关类型
// ===========================

/**
 * 协议链接项
 */
export interface FooterAgreementItem {
  name: string;
  link: string;
}

/**
 * 官方链接项
 */
export interface FooterOfficialLinkItem {
  icon: string;
  link: string;
}

/**
 * 合作伙伴项
 */
export interface FooterPartnerItem {
  icon: string;
  link: string;
  sort: number;
}

/**
 * Footer 配置
 */
export interface FooterConfig {
  /** 协议列表（如用户协议、隐私政策） */
  agreementList?: FooterAgreementItem[];
  /** 官方链接标题 */
  officialLinkTitle?: string;
  /** 官方链接列表（社媒等） */
  officialLinkList?: FooterOfficialLinkItem[];
  /** 平台描述文案 */
  platformDescription?: string;
  /** 合作伙伴列表（按 sort 排序） */
  partnerList?: FooterPartnerItem[];
}

// ===========================
// 平台配置类型
// ===========================

/**
 * 平台Logo配置
 */
export interface PlatformLogo {
  logoUrl: string;
  facebookPage: string;
  facebookGroup: string;
  telegram: string;
  whatsapp: string;
  /** 下载 App 弹窗中间文案，来自 home_display/config */
  downloadPagePopupConfig?: string;
  /** 是否显示下载 App 弹窗，为 true 时才显示 */
  enableDownloadAppPopup?: boolean;
  enableDownloadAppFloatingOnHomeTop?: boolean;
  /** 下载按钮文案 */
  appDownloadTitle?: string;
  /** Android 下载方式：APK 直接下载 | GP 跳转 Google Play */
  appDownloadMethod?: "APK" | "GP";
  /** APK 文件的资源 ID（appDownloadMethod 为 APK 时使用） */
  apkFile?: string;
  /** Google Play 跳转链接（appDownloadMethod 为 GP 时使用） */
  gpLink?: string;
  /** iOS下载开关 */
  enableIosDownload?: boolean;
  /** iOS下载链接 */
  iosDownloadLink?: string;
  // 顶部：优先下载 APK
  preferApkDownload: boolean;

  // 底部：隐藏 PWA 下载按钮
  hidePwaDownloadButton: boolean;
  /** 底部导航中间 Invite 图标（可配置 CDN URL） */
  bottomNavInviteIconUrl?: string;
}

/**
 * 客服类型
 */
export type CustomerCategory = "ONLINE_SERVICE" | "CUSTOM_SERVICE";

/**
 * 客服服务类型
 */
export type CustomerServiceType = "TAWK_TO" | "LIVE_CHAT" | "ZENDESK" | string;

/**
 * 客服配置项
 */
export interface CustomerServiceItem {
  itemId: string;
  sort: number;
  customerCategory: CustomerCategory;
  title: string;
  description?: string;
  buttonText?: string;
  iconUrl?: string;
  serviceType?: CustomerServiceType | null;
  propertyId?: string | null;
  widgetId?: string | null;
  licenseId?: string | null;
  jumpUrl?: string;
}

/**
 * 代理邀请排行榜档位
 */
export interface LeaderboardTier {
  rank: string;
  rewardAmount: number;
}

/**
 * 代理邀请配置
 */
export interface AgentInvitation {
  /** 月度门槛 */
  monthlyThreshold: number;
  /** 月度基数 */
  monthlyBasis: number;
  /** 邀请奖励表达式 */
  invitationBonusExpr: string;
  /** 邀请奖励倍率 */
  invitationBonusMultiplier: number;
  /** 成就奖励表达式 */
  achievementRewardExpr: string;
  /** 成就奖励倍率 */
  achievementRewardMultiplier: number;
  /** 投注返利表达式 */
  bettingRebatesExpr: string;
  /** 投注返利倍率 */
  bettingRebateMultiplier: number;
  /** 排行榜档位 */
  leaderboardTiers: LeaderboardTier[];
  /** 富文本内容 */
  richTextContent?: string;
  homeTopInviteText?: string;
  homeBottomTabAmount?: string;
}

// ===========================
// API 响应类型
// ===========================

/**
 * 注册配置（旧版，保留兼容）
 */
export interface RegisterDisplayConfig {
  id: string;
  created: number;
  modified: number;
  showEmail: boolean;
  showInviteCode: boolean;
  showEmailRequired: boolean;
}

/**
 * 登录注册配置类型
 */
export type LoginRegisterConfigType = "SYSTEM_DEFAULT" | "MANUAL";

/**
 * KYC 域名配置（域名与提现引导阈值一对一绑定）
 */
export interface KycDomainConfig {
  domain: string;
  withdrawGuideCount: number;
}

/**
 * KYC配置
 */
export interface KycConfig {
  id: string;
  created: number;
  modified: number;
  countryId: string;
  abFlag: boolean;
  kycType: string;
  enabled: boolean;
  waitForCallback: boolean;
  callbackTitle: string;
  callbackContent: string;
  allowEdit: boolean;
  editable?: boolean; // KYC 可编辑开关
  rechargeGuideEnabled?: boolean; // 充值时未认证引导开关
  withdrawGuideEnabled?: boolean; // 提现时未认证引导开关
  domainRestricted?: boolean; // 是否按域名区分KYC
  domainConfigs?: KycDomainConfig[]; // 域名与提现阈值绑定列表
}

/**
 * 国家/跳转配置项（api/home_display/config 的 redirectConfigs 项）
 */
export interface RedirectConfigItem {
  id: string;
  countryRedirectId?: string;
  countryName?: string;
  countryIcon?: string;
  displayName?: string;
  redirectUrl?: string;
  [key: string]: any;
}

/**
 * 登录注册配置项
 */
export interface LoginRegisterConfig {
  /** 配置ID */
  id?: string;
  /** 匹配的域名（支持通配符 *） */
  domain?: string;

  // 登录相关配置
  /** 启用手机号登录 */
  enableLoginMobile?: boolean;
  /** 启用邮箱登录 */
  enableLoginEmail?: boolean;
  /** 启用手机验证码登录 */
  enableLoginMobileVerifyCode?: boolean;

  // 注册相关配置
  /** 启用手机号注册 */
  enableMobileRegister?: boolean;
  /** 启用邮箱注册 */
  enableEmailRegister?: boolean;
  /** 手机号是否必填（与邮箱二选一或同时展示时生效） */
  enableMobileRequired?: boolean;
  /** 邮箱是否必填（与手机二选一或同时展示时生效） */
  enableEmailRequired?: boolean;
  /** 注册时手机验证码是否必填 */
  enableRegisterMobileVerify?: boolean;
  /** 显示邀请码输入框 */
  enableRegisterInviteCode?: boolean;
  /** 是否显示「确认密码」输入框（二次输入密码确认），关闭则不显示该字段 */
  enableSecondPasswordConfirm?: boolean;
  /** 注册场所等配置（与二次确认无关，供其他逻辑使用） */
  registerPlaces?: string[];

  // 重置密码相关配置
  /** 启用重置密码功能（手机号重置） */
  enableMobileResetPassword?: boolean;
  /** 启用邮箱重置密码功能 */
  enableEmailResetPassword?: boolean;
  /** 支持的重置方式 */
  resetPasswordMethods?: ("mobile" | "email")[];

  // 协议相关
  /** 是否默认勾选协议 */
  enableDefaultCheckProtocol?: boolean;
  /** 协议展示文案（支持 Markdown 链接 [文字](/protocol/xxx)） */
  defaultProtocolText?: string;
  configType: LoginRegisterConfigType;
  domainId: string[];
  enableRegisterKycInfo?: boolean;
}

/**
 * 首页展示配置响应数据
 */
export interface HomeDisplayConfig {
  displayItems: DisplayItem[];
  popupTimings: PopupTiming[];
  popupPriorities: PopupPriority[];
  activities?: ActivitiesConfig;
  /** 平台Logo配置 */
  platformLogo?: PlatformLogo;
  /** Footer 配置 */
  footer?: FooterConfig;
  /** 客服配置列表 */
  customerServices?: CustomerServiceItem[];
  /** 代理邀请配置 */
  agentInvitation?: AgentInvitation;
  /** 注册配置（旧版，保留兼容） */
  displayConfig?: RegisterDisplayConfig;
  /** 登录注册配置列表（新版） */
  loginRegisterConfigs?: LoginRegisterConfig[];
  /** KYC配置 */
  kycConfig?: KycConfig;
  /** 国家/跳转列表（用于国家列表弹窗等） */
  redirectConfigs?: RedirectConfigItem[];
  /** 下载 App 弹窗说明文案（有则用，无则用 i18n app.downloadPopupNotice） */
  downloadPagePopupConfig?: string;
  /** 渠道配置类型 */
  channelConfigTypes?: Record<string, boolean>;
  /** 手机号重置密码 IP 冷却中（尝试次数达上限），为 true 时需展示“次数过多”提示弹窗 */
  resetPasswordIpInCooldown?: boolean;
  /** 邮箱重置密码 IP 冷却中（尝试次数达上限），为 true 时需展示“次数过多”提示弹窗 */
  resetPasswordEmailIpInCooldown?: boolean;
}

/**
 * API响应格式
 */
export interface HomeDisplayConfigResponse {
  code: number;
  message?: string;
  model: HomeDisplayConfig;
}

// ===========================
// 前端使用辅助类型
// ===========================

/**
 * 按位置分组的Display Items
 */
export type GroupedDisplayItems = Partial<
  Record<DisplayPosition, DisplayItem[]>
>;

/**
 * 按时机分组的Popup Timings
 */
export type GroupedPopupTimings = Partial<Record<TimingType, PopupTiming>>;

/**
 * 弹窗展示记录（用于控制每日弹窗）
 */
export interface PopupRecord {
  id: string;
  lastShownDate: string;
  count: number;
}

/**
 * 弹窗队列项
 */
export interface PopupQueueItem {
  id: string;
  type: "activity" | "custom";
  category: PopupCategory;
  priority: number;
  data: ActivityPopupItem | CustomPopupItem;
}
